import EXIF from '@/utils/exif.js'
import dataURLtoBlob from 'blueimp-canvas-to-blob'

export default class {
  file = null
  maxWidth = 1024
  maxHeight = 1024
  maxSize = null
  quality = 1.0
  scaleRatio = null
  autoRotate = false
  outputFormat = 'string'
  doNotResize = []
  debug = 0
  dimensions = {}
  exifData = {}

  constructor (file, options = null) {
    this.file = file

    if (options) {
      if (Object.prototype.hasOwnProperty.call(options, 'maxWidth')) {
        this.maxWidth = options.maxWidth
      }
      if (Object.prototype.hasOwnProperty.call(options, 'maxHeight')) {
        this.maxHeight = options.maxHeight
      }
      if (Object.prototype.hasOwnProperty.call(options, 'maxSize')) {
        this.maxSize = options.maxSize
      }
      if (Object.prototype.hasOwnProperty.call(options, 'quality')) {
        this.quality = options.quality
      }
      if (Object.prototype.hasOwnProperty.call(options, 'scaleRatio')) {
        this.scaleRatio = options.scaleRatio
      }
      if (Object.prototype.hasOwnProperty.call(options, 'autoRotate')) {
        this.autoRotate = options.autoRotate
      }
      if (Object.prototype.hasOwnProperty.call(options, 'outputFormat')) {
        this.outputFormat = options.outputFormat
      }
      if (Object.prototype.hasOwnProperty.call(options, 'doNotResize')) {
        this.doNotResize = options.doNotResize
      }
      if (Object.prototype.hasOwnProperty.call(options, 'debug')) {
        this.debug = options.debug
      }
    }
  }

  static removeMimeFromBase64 (image) {
    return image.replace(/^data:[a-z]+\/[a-z]+;base64,/, '')
  }

  resize (width = 1024, height = 1024) {
    this.maxWidth = width
    this.maxHeight = height

    const file = this.file
    this.log('handleFile() is called with file:', 2, file)

    const mimetype = file.type.split('/')
    const isImage = mimetype[0] === 'image'
    const doNotResize = typeof this.doNotResize === 'string' ? [this.doNotResize] : this.doNotResize

    return new Promise((resolve) => {
      if (!isImage || doNotResize.includes('*') || doNotResize.includes(mimetype[1])) {
        this.log('No Resize, return file directly')
        resolve(file)
        this.emitEvent(file) // does NOT respect the output format prop
        this.emitComplete()
      } else {
        const that = this
        const img = document.createElement('img')
        const reader = new window.FileReader()

        reader.onload = function (e) {
          that.log('reader.onload() is triggered', 2)

          img.src = e.target.result
          img.onload = function () {
            that.log('img.onload() is triggered', 2)

            // this extracts exifdata if available. Returns an empty object if not
            EXIF.getData(img, function () {
              that.exifData = this.exifdata
              if (Object.keys(that.exifData).length === 0) {
                that.log('ImageUploader: exif data found and extracted', 2)
              }
            })

            const scaleImage = that.scaleImage(img, that.exifData.Orientation)
            resolve(scaleImage)
          }
        }
        reader.readAsDataURL(file)
      }
    })
  }

  scaleImage (img, orientation = 1) {
    this.log('scaleImage() is called', 2)

    let canvas = document.createElement('canvas')
    canvas.width = img.width
    canvas.height = img.height
    const ctx = canvas.getContext('2d')
    ctx.save()

    if (this.autoRotate && orientation > 1) {
      this.log('ImageUploader: rotating image as per EXIF orientation tag = ' + orientation)
      const width = canvas.width
      const styleWidth = canvas.style.width
      const height = canvas.height
      const styleHeight = canvas.style.height

      if (orientation > 4) {
        canvas.width = height
        canvas.style.width = styleHeight
        canvas.height = width
        canvas.style.height = styleWidth
      }
      switch (orientation) {
        case 2:
          ctx.translate(width, 0)
          ctx.scale(-1, 1)
          break
        case 3:
          ctx.translate(width, height)
          ctx.rotate(Math.PI)
          break
        case 4:
          ctx.translate(0, height)
          ctx.scale(1, -1)
          break
        case 5:
          ctx.rotate(0.5 * Math.PI)
          ctx.scale(1, -1)
          break
        case 6:
          ctx.rotate(0.5 * Math.PI)
          ctx.translate(0, -height)
          break
        case 7:
          ctx.rotate(0.5 * Math.PI)
          ctx.translate(width, -height)
          ctx.scale(-1, 1)
          break
        case 8:
          ctx.rotate(-0.5 * Math.PI)
          ctx.translate(-width, 0)
          break
      }
    }
    ctx.drawImage(img, 0, 0)
    ctx.restore()

    const ratio = canvas.width / canvas.height
    let mWidth = Math.min(this.maxWidth, ratio * this.maxHeight)

    if (this.maxSize > 0 && this.maxSize < (canvas.width * canvas.height) / 1000000) {
      const mSize = Math.floor(Math.sqrt(this.maxSize * ratio) * 1000)
      mWidth = mWidth > 0 ? Math.min(mWidth, mSize) : mSize
    }

    if (this.scaleRatio) {
      mWidth = Math.min(mWidth, Math.floor(this.scaleRatio * canvas.width))
    }

    this.dimensions.orgWidth = canvas.width
    this.dimensions.orgHeight = canvas.height
    this.dimensions.width = mWidth
    this.dimensions.height = Math.floor(mWidth / ratio)

    this.log('ImageUploader: original image size = ' + canvas.width + ' X ' + canvas.height)
    this.log('ImageUploader: scaled image size = ' + mWidth + ' X ' + Math.floor(mWidth / ratio))

    if (mWidth <= 0) {
      mWidth = 1
      console.warning('ImageUploader: image size is too small')
    }

    while (canvas.width >= 2 * mWidth) {
      canvas = this.getHalfScaleCanvas(canvas)
    }

    if (canvas.width > mWidth) {
      canvas = this.scaleCanvasWithAlgorithm(canvas, mWidth)
    }

    const quality = this.file.type === 'image/jpeg' ? this.quality : 1.0
    const imageData = canvas.toDataURL(this.file.type, quality)
    if (typeof this.onScale === 'function') {
      this.onScale(imageData)
    }

    this.log('New ImageData is ready', 2)

    return this.formatOutput(imageData)
  }

  scaleCanvasWithAlgorithm (canvas, maxWidth) {
    const scaledCanvas = document.createElement('canvas')
    const scale = maxWidth / canvas.width

    scaledCanvas.width = canvas.width * scale
    scaledCanvas.height = canvas.height * scale

    const srcImgData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height)
    const destImgData = scaledCanvas.getContext('2d').createImageData(scaledCanvas.width, scaledCanvas.height)

    this.applyBilinearInterpolation(srcImgData, destImgData, scale)

    scaledCanvas.getContext('2d').putImageData(destImgData, 0, 0)

    return scaledCanvas
  }

  applyBilinearInterpolation (srcCanvasData, destCanvasData, scale) {
    function inner (f00, f10, f01, f11, x, y) {
      const unX = 1.0 - x
      const unXY = 1.0 - y
      return f00 * unX * unXY + f10 * x * unXY + f01 * unX * y + f11 * x * y
    }
    let i, j
    let iyv, iy0, iy1, ixv, ix0, ix1
    let idxD, idxS00, idxS10, idxS01, idxS11
    let dx, dy
    let r, g, b, a
    for (i = 0; i < destCanvasData.height; ++i) {
      iyv = i / scale
      iy0 = Math.floor(iyv)
      iy1 = Math.ceil(iyv) > srcCanvasData.height - 1 ? srcCanvasData.height - 1 : Math.ceil(iyv)
      for (j = 0; j < destCanvasData.width; ++j) {
        ixv = j / scale
        ix0 = Math.floor(ixv)
        // Math.ceil can go over bounds
        ix1 = Math.ceil(ixv) > srcCanvasData.width - 1 ? srcCanvasData.width - 1 : Math.ceil(ixv)
        idxD = (j + destCanvasData.width * i) * 4
        // matrix to vector indices
        idxS00 = (ix0 + srcCanvasData.width * iy0) * 4
        idxS10 = (ix1 + srcCanvasData.width * iy0) * 4
        idxS01 = (ix0 + srcCanvasData.width * iy1) * 4
        idxS11 = (ix1 + srcCanvasData.width * iy1) * 4
        // overall coordinates to unit square
        dx = ixv - ix0
        dy = iyv - iy0
        // I let the r, g, b, a on purpose for debugging
        r = inner(srcCanvasData.data[idxS00], srcCanvasData.data[idxS10], srcCanvasData.data[idxS01], srcCanvasData.data[idxS11], dx, dy)
        destCanvasData.data[idxD] = r

        g = inner(srcCanvasData.data[idxS00 + 1], srcCanvasData.data[idxS10 + 1], srcCanvasData.data[idxS01 + 1], srcCanvasData.data[idxS11 + 1], dx, dy)
        destCanvasData.data[idxD + 1] = g

        b = inner(srcCanvasData.data[idxS00 + 2], srcCanvasData.data[idxS10 + 2], srcCanvasData.data[idxS01 + 2], srcCanvasData.data[idxS11 + 2], dx, dy)
        destCanvasData.data[idxD + 2] = b

        a = inner(srcCanvasData.data[idxS00 + 3], srcCanvasData.data[idxS10 + 3], srcCanvasData.data[idxS01 + 3], srcCanvasData.data[idxS11 + 3], dx, dy)
        destCanvasData.data[idxD + 3] = a
      }
    }
  }

  getHalfScaleCanvas (canvas) {
    const halfCanvas = document.createElement('canvas')
    halfCanvas.width = canvas.width / 2
    halfCanvas.height = canvas.height / 2

    halfCanvas.getContext('2d').drawImage(canvas, 0, 0, halfCanvas.width, halfCanvas.height)

    return halfCanvas
  }

  formatOutput (imageData) {
    this.log('ImageUploader: outputFormat: ' + this.outputFormat)

    if (this.outputFormat === 'file') {
      return this.file
    }

    if (this.outputFormat === 'blob') {
      if (typeof dataURLtoBlob === 'undefined') {
        console.warn('Missing library! blueimp-canvas-to-blob.js must be loaded to output as "blob"')
        console.warn('Falling back to default base64 dataUrl')
        return imageData
      }
      return dataURLtoBlob(imageData)
    }

    const info = {
      name: this.file.name,
      type: this.file.type,
      // size: this.file.size,
      newWidth: this.dimensions.width,
      newHeight: this.dimensions.height,
      orgWidth: this.dimensions.orgWidth,
      orgHeight: this.dimensions.orgHeight,
      aspectRatio: Math.round((this.dimensions.width / this.dimensions.height) * 100) / 100, // as Float
      modifiedTimestamp: this.file.lastModified,
      modifiedDate: this.file.lastModifiedDate
    }

    // return just info
    if (this.outputFormat === 'info') {
      return info
    }

    if (this.outputFormat === 'verbose') {
      const data = {
        dataUrl: imageData,
        info,
        exif: Object.keys(this.exifData).length > 0 ? this.exifData : null
      }
      return data
    }

    // simple base64 dataUrl string by default
    return imageData
  }

  log (msg, level = 1, details = null) {
    if (this.debug >= level) {
      // eslint-disable-next-line
      console.info(msg)
      if (details) {
        // eslint-disable-next-line
        console.info(details)
      }
    }
  }
}
